import { BalanceType } from '@pp/core/src/api/fetchers/userInfo/types';
import { PopupType } from '@pp/core/src/components/popup/types';
import { CrossOriginCommunication } from '@pp/core/src/controllers/crossOriginCommunication/CrossOriginCommunicationController';
import { OutgoingMessage } from '@pp/core/src/controllers/crossOriginCommunication/types';
import { CoreBetsOpenGameCommunicationProcessor } from '@pp/core/src/controllers/gameCommunication/processors/CoreBetsOpenGameCommunicationProcessor';
import type {
  GameCommunicationMessageType,
  IGameCommunicationProcessor,
} from '@pp/core/src/controllers/gameCommunication/types';
import { Logger } from '@pp/core/src/logger/Logger';
import { AutoplayStatus } from '@pp/core/src/store/slices/autoplay/types';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import { BetType, BetsSliceType } from '@pp/core/src/store/slices/bets/types';
import { gameStateActions } from '@pp/core/src/store/slices/game/slice';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { popupActions } from '@pp/core/src/store/slices/popup/slice';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';
import { countTotalBet } from '@pp/core/src/utils/bets';
import { roundNumber } from '@pp/core/src/utils/number';

import { BetIds, Bets } from '../../../config';
import { GameStore } from '../../../store/GameStore';
import { autoCashoutActions } from '../../../store/slices/autocashout/slice';
import { betStepperActions } from '../../../store/slices/betStepper/slice';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';
import { highflyerStateActions } from '../../../store/slices/highflyerState/slice';
import { GameCommunicationBetSender } from '../senders';

const betSender = new GameCommunicationBetSender();
export class GameCommunicationHFBetsOpenProcessor
  extends CoreBetsOpenGameCommunicationProcessor
  implements IGameCommunicationProcessor
{
  delay = 0;
  process(message: GameCommunicationMessageType): void {
    const {
      highflyerState,
      gameState: { userInfo, currencyInfo },
    } = this.store.getState();
    const hasAnyEarlyBets = highflyerState.earlyBetAmount[Bets.ONE] > 0 || highflyerState.earlyBetAmount[Bets.TWO] > 0;
    CrossOriginCommunication.dispatch(OutgoingMessage.RoundStart);

    const state = this.store.getState();
    const gameState = state.gameState;
    const balance = gameState.userInfo?.balance;
    const bets = state.bets;

    if (this.checkLowBalanceAndUpdateState(balance, 0, bets.betLimits)) {
      if (gameState.balanceCheckCount > 3) {
        this.store.dispatch(popupActions.pushPopup(PopupType.InsufficientFunds));
      } else {
        this.store.dispatch(gameStateActions.setBalanceCheckCount(gameState.balanceCheckCount + 1));
      }
    }

    this.store.dispatch(highflyerStateActions.setIsSingleBetPlaced(false));

    //Updating the betStack based on the early bets
    this.store.dispatch(betActions.resetBetStack());
    if (hasAnyEarlyBets) {
      const earlyBets = this.getEarlyBets(this.store);
      this.store.dispatch(
        betActions.pushBet({
          bet: earlyBets,
          balance: (userInfo?.balance.amount || 0) + (userInfo?.balance.bonus || 0),
          decimals: currencyInfo?.digitsNumber || 2,
          freeChipValue: 0,
          freeChipBalance: 0,
        })
      );
      this.placeEarlyBets();
    }

    //Resetting isBetPlaced status for crashed betboards
    BetIds.forEach((betId) => {
      if (highflyerState.earlyBetAmount[betId] === 0) {
        this.store.dispatch(highflyerStateActions.resetIsBetPlaced(betId));
      }
    });
    this.checkAutoplayAvailability(balance, bets);

    this.store.dispatch(highflyerStateActions.setMultiplier(null));
    this.store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.PlaceYourBets));
    const isMegaWin = message?.megawin === 'true';
    this.store.dispatch(gameStateActions.setWinResult({ amount: 0, updateTimestamp: Date.now(), isMegaWin }));
    setTimeout(() => {
      this.store.dispatch(gameStateActions.resetForNewGame());
    }, 3000);
    this.placeAutoplayBet(this.store);

    //Resetting the earlybet amounts
    BetIds.forEach((betCode) => {
      this.store.dispatch(highflyerStateActions.setEarlyBetAmount({ betCode, amount: 0 }));
      this.store.dispatch(highflyerStateActions.setCashedAmount({ betCode: betCode, amount: 0 }));
      this.store.dispatch(highflyerStateActions.resetIsDealingInProgress(betCode));
      this.store.dispatch(highflyerStateActions.resetLocalCashedMul(betCode));
      this.store.dispatch(betStepperActions.resetBetStack(betCode));
    });
  }

  protected placeEarlyBets(): void {
    betSender.dispatch('');
  }

  public checkAutoplayAvailability(balance: BalanceType | undefined, bets: BetsSliceType): void {
    const state = this.store.getState();
    const { crashAutoplay } = this.store.getState();
    // const balance = this.state.gameState.userInfo?.balance;
    Object.keys(bets?.betList || {})
      .map(Number)
      .forEach((sBet) => {
        const betId = bets.betList ? bets.betList[sBet] : 1;
        const crashAutoPlayInProgress = crashAutoplay.isInProgress[betId];
        if (crashAutoPlayInProgress) {
          const totalBet = countTotalBet(
            crashAutoplay.savedBet[betId]!.betType.betSpots || {},
            state.gameState.currencyInfo?.digitsNumber || 0
          );
          const savedBetSpots = (crashAutoplay.savedBet[betId]!.betType.betSpots?.[betId]?.value ?? 0) > 0;
          const allBetsUnavailable = savedBetSpots ? state.bets.disabledBetSpots.includes(betId.toString()) : null;
          this.stopAutoplay(betId, allBetsUnavailable, this.store, 'AUTOPLAY_PREFERENCES_STOPPED', 'BetsUnavailable');
          const autoPlayStop = totalBet > (balance?.amount || 0);
          this.stopAutoplay(betId, autoPlayStop, this.store, 'AUTOPLAY_SUPPORT_CURR_BAL', 'NoMoney');
        }
      });
  }

  protected stopAutoplay(betId: Bets, check: boolean | null, store: GameStore, msg: string, LoggerMsg: string): void {
    if (check) {
      store.dispatch(crashAutoplayActions.setInfiniteAutoplayEnabled(betId));
      store.dispatch(crashAutoplayActions.stop({ betsSpot: Number(betId) }));
      store.dispatch(toastActions.createToast(msg));
      store.dispatch(highflyerStateActions.resetIsBetPlaced(betId));
      Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, LoggerMsg, false);
    }
  }

  protected placeAutoplayBet(store: GameStore): void {
    const { crashAutoplay, bets, gameState } = store.getState();

    Object.keys(bets?.betList || {})
      .map(Number)
      .forEach((sBet) => {
        const betId = bets.betList ? bets.betList[sBet] : 1;
        const crashAutoPlayInProgress = crashAutoplay.isInProgress[betId];
        const crashAutoIsthisRoundOnly = crashAutoplay.isThisRoundOnly[betId];
        let modifyiedPopIsOpen = false;
        const currentPopup = store.getState().popup.currentPopup?.popupName;

        if (
          (crashAutoPlayInProgress ||
            (crashAutoplay.autoplayStatus === AutoplayStatus.Paused && !!bets.acceptedBets.length)) &&
          currentPopup === `CrashAutoplayModified_${betId}`
        ) {
          modifyiedPopIsOpen = true;
        }
        if (
          crashAutoPlayInProgress &&
          !crashAutoIsthisRoundOnly &&
          !crashAutoplay.isCancelled[betId] &&
          !modifyiedPopIsOpen
        ) {
          store.dispatch(betActions.clearBet(betId)); // only single bet should set accordingly the auto play kept bet id

          const balance = gameState.userInfo?.balance;
          const deductibleBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
          const disabledBets = bets.disabledBetSpots ?? [];
          const newBet: BetType = { betSpots: {} };
          if (
            crashAutoplay.savedBet[betId]!.betType.betSpots[betId].value > 0 &&
            !disabledBets.includes(betId.toString())
          ) {
            newBet.betSpots[betId] = crashAutoplay.savedBet[betId]!.betType.betSpots[betId];
          }
          if (newBet.betSpots[betId]) {
            store.dispatch(
              autoCashoutActions.setAutoCashoutButton({
                betCode: betId,
                isEnabled: crashAutoplay.savedBet[betId].isEnable,
              })
            );
            store.dispatch(
              autoCashoutActions.setCashoutMultiplier({
                betCode: betId,
                cashoutMultiplier: crashAutoplay.savedBet[betId].value,
              })
            );
            // check proper value
            store.dispatch(
              betStepperActions.setBetInputValue({
                betId: Number(betId),
                value: roundNumber(newBet.betSpots[betId].value, 2).toFixed(2),
              })
            );
            store.dispatch(
              betActions.pushBet({
                bet: newBet,
                balance: deductibleBalance,
                decimals: gameState.currencyInfo?.digitsNumber ?? 2,
                freeChipValue: 0,
                freeChipBalance: 0,
              })
            );
            betSender.dispatch('');
            store.dispatch(highflyerStateActions.setIsBetPlaced(betId));
          }
        }
      });
  }

  protected getEarlyBets(store: GameStore): BetType {
    const { highflyerState } = store.getState();
    const earlyBets: BetType = {
      betSpots: {},
    };
    BetIds.forEach((betCode) => {
      earlyBets.betSpots = {
        ...earlyBets.betSpots,
        [betCode]: {
          value: highflyerState.earlyBetAmount[betCode],
        },
      };
    });
    return earlyBets;
  }
}
